import { useQuery } from '@tanstack/react-query';
import { api, clinicApi } from '@/lib/api';
import type { ItemType, MedicineSuggestion } from '@/types/api';

interface UseMedicineSuggestParams {
  query: string;
  type?: ItemType;
  limit?: number;
  /** When false, the query stays idle. Default: true. */
  enabled?: boolean;
}

/**
 * Aggregated typeahead — backs the brand-name autosuggest on the new-item
 * stock-in form. Pulls a single merged list from existing clinic items +
 * `medicine_reference`. Stays idle while the trimmed query is shorter than
 * 2 chars (trigram results below that length are noise).
 *
 * Caller debounces the input (see `useDebounced`); this hook does not.
 */
export function useMedicineSuggest(params: UseMedicineSuggestParams) {
  const { query, type, limit = 8, enabled = true } = params;
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['medicine-suggest', { q: trimmed, type, limit }],
    queryFn: () => {
      const search = new URLSearchParams({ q: trimmed, limit: String(limit) });
      if (type) search.set('type', type);
      return clinicApi.get<MedicineSuggestion[]>(`medicine-suggest?${search.toString()}`);
    },
    enabled: enabled && trimmed.length >= 2,
    staleTime: 30_000,
  });
}
